import React, { useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Target, AlertTriangle, ChevronRight, Bookmark } from "lucide-react";
import HypothesisDetail, { money } from "./HypothesisDetail";

const MAX_OPTIONS = 5;
const STATUS_PILL = {
  primary: { label: "Primary direction", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  saved: { label: "Saved", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  rejected: { label: "Rejected", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const scoreTone = (score) => (score >= 70 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-rose-500");

const HypothesisTile = ({ h, archetype, index, onOpen }) => {
  const pill = STATUS_PILL[h.status];
  return (
    <button onClick={onOpen} data-testid={`hypothesis-tile-${index}`}
      className="card-soft card-soft-hover accent-bar w-full p-4 pl-5 text-left sm:p-5 sm:pl-6"
      style={{ "--accent": h.status === "primary" ? "#059669" : "#0284C7" }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[10px] font-mono uppercase tracking-wider text-sky-700">{archetype?.name || h.archetype_id}</span>
          <h3 className="mt-1 font-bold text-slate-900 break-words leading-snug" data-testid={`hypothesis-title-${h.id}`}>{h.title}</h3>
        </div>
        {h.fit_view?.score != null && (
          <div className="shrink-0 text-right" data-testid={`hypothesis-fit-score-${h.id}`}>
            <p className={`num-display text-2xl leading-none ${scoreTone(h.fit_view.score)}`}>{h.fit_view.score}</p>
            <p className="text-[9px] uppercase tracking-wide text-slate-400 mt-1">Fit score</p>
          </div>
        )}
      </div>

      <p className="mt-2.5 text-sm text-slate-600 line-clamp-2">{h.offer || h.target_customer}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {pill && <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${pill.cls}`}>{pill.label}</span>}
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] text-slate-600">{money(h.price_hypothesis)}</span>
        {!!h.constraint_conflicts?.length && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
            <AlertTriangle className="w-3 h-3" /> {h.constraint_conflicts.length} conflict{h.constraint_conflicts.length > 1 ? "s" : ""}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-sky-700">Review <ChevronRight className="w-3.5 h-3.5" /></span>
      </div>
    </button>
  );
};

export const StepDirection = ({ state, patch, onNext }) => {
  const [busy, setBusy] = useState(false);
  const [building, setBuilding] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const hyps = (state.hypotheses || []).slice(0, MAX_OPTIONS);
  const primary = hyps.find((h) => h.status === "primary");
  const archetypeOf = (id) => (state.config?.archetypes || []).find((a) => a.id === id);

  const act = async (h, action) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/accelerator/hypotheses/${h.id}`, { action });
      patch({
        hypotheses: (state.hypotheses || []).map((x) => (x.id === h.id ? data.hypothesis : action === "primary" && x.status === "primary" ? { ...x, status: "saved" } : x)),
        progress: data.progress,
      });
      if (action === "primary") {
        toast.success("Primary direction saved — your choice, not ours");
        setOpenIndex(null);
      }
      if (action === "save") toast.success("Saved for later");
      if (action === "reject") { toast.success("Rejected"); setOpenIndex(null); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const buildBlueprint = async () => {
    setBuilding(true);
    try {
      const { data } = await api.post("/accelerator/blueprint");
      patch({ blueprint: data.blueprint, progress: data.progress });
      toast.success("Your 2nd Income Blueprint is drafted — edit anything");
      onNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build your blueprint");
    } finally {
      setBuilding(false);
    }
  };

  if (openIndex != null && hyps[openIndex]) {
    const h = hyps[openIndex];
    return (
      <section data-testid="accelerator-step-direction-panel">
        <HypothesisDetail
          h={h}
          archetype={archetypeOf(h.archetype_id)}
          index={openIndex}
          total={hyps.length}
          busy={busy}
          onAction={act}
          onBack={() => setOpenIndex(null)}
          onPrev={() => setOpenIndex(Math.max(0, openIndex - 1))}
          onNext={() => setOpenIndex(Math.min(hyps.length - 1, openIndex + 1))}
        />
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="accelerator-step-direction-panel">
      <div className="card-soft p-5 sm:p-6">
        <h2 className="text-base md:text-lg font-bold text-slate-900">Step 4 — Choose your direction</h2>
        <p className="text-sm text-slate-600 mt-1.5">Here's what your experience suggests could be tested. Open one route at a time, read it properly, then choose what feels most relevant — the highest score is not automatically the right answer.</p>
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-500">
          <Bookmark className="w-3.5 h-3.5 text-sky-600" /> {hyps.length} route{hyps.length === 1 ? "" : "s"} to review
        </p>
      </div>

      <div className="grid gap-3">
        {hyps.map((h, i) => (
          <HypothesisTile key={h.id} h={h} index={i} archetype={archetypeOf(h.archetype_id)} onOpen={() => setOpenIndex(i)} />
        ))}
      </div>

      <div className="card-soft p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600" data-testid="accelerator-primary-summary">
          {primary ? <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700"><Target className="w-4 h-4" /> Primary: {primary.title}</span> : "Open a route and select one direction to continue."}
        </p>
        <Button onClick={buildBlueprint} disabled={!primary || building} className="h-11 bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50" data-testid="accelerator-build-blueprint">
          {building ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…</> : "Build my blueprint"}
        </Button>
      </div>
    </section>
  );
};

export default StepDirection;
